import { useCallback, useMemo, useState } from 'react';

import {
  useMarkAllNotificationsReadMutation,
  useMarkNotificationsReadMutation,
  useNotificationFeedQuery,
} from 'gqlhooks';

import { TNotificationsFilter } from 'core/state';

export interface INotificationRow {
  id: string;
  kind: string;
  title: string;
  body: string;
  taskId: string;
  priority: string;
  dueDate?: string | null;
  actorName?: string | null;
  createdAt: string;
  readAt?: string | null;
}

export interface IUseNotificationFeed {
  rows: INotificationRow[];
  unreadCount: number;
  hasNextPage: boolean;
  loading: boolean;
  error: boolean;
  loadMore: () => void;
  markRead: (ids: string[]) => void;
  markAllRead: () => void;
  refetch: () => void;
}

const PAGE = 20;

/**
 * Лента колокольчика (ТЗ 0068).
 *
 * Грузится ПО ОТКРЫТИЮ панели, а не в опросе: в фоне ходит только счётчик.
 *
 * Вкладка «Непрочитанные» уходит на сервер аргументом `unreadOnly`, а не
 * фильтрует уже загруженное: иначе, как только непрочитанных станет больше,
 * чем помещается на страницу, число на бейдже и длина списка разъедутся.
 */
export const useNotificationFeed = (
  filter: TNotificationsFilter,
  skip: boolean,
  /**
   * Освежить счётчик колокольчика (ТЗ 0068).
   *
   * 🔴 Обязательный, а не «на всякий случай». Бейдж живёт ОТДЕЛЬНЫМ запросом
   * (лёгким, его и опрашивают раз в минуту), и Apollo сам их не связывает:
   * без этого вызова человек отмечает уведомление прочитанным, список
   * обновляется, а число на колокольчике висит прежним до следующего опроса —
   * то есть до минуты. Выглядит как «кнопка не сработала».
   */
  onChanged: () => void,
): IUseNotificationFeed => {
  /**
   * Мгновенная реакция на «прочитано» — единственное место, где клиент вообще
   * трогает число. Пока ответ мутации не пришёл, помеченные строки уже
   * показаны прочитанными, а бейдж уменьшен; после ответа и то и другое снова
   * целиком серверное.
   */
  const [pendingRead, setPendingRead] = useState<Set<string>>(new Set());

  const { data, loading, error, fetchMore, refetch } = useNotificationFeedQuery(
    {
      variables: { first: PAGE, unreadOnly: filter === 'unread' },
      skip,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    },
  );

  const [markReadMutation] = useMarkNotificationsReadMutation();
  const [markAllMutation] = useMarkAllNotificationsReadMutation();

  const page = data?.staffNotifications;

  const rows = useMemo<INotificationRow[]>(
    () =>
      (page?.edges ?? [])
        .map((edge) => edge.node)
        .map((node) => ({
          id: String(node.id),
          kind: node.kind,
          title: node.title,
          body: node.body,
          taskId: String(node.taskId),
          priority: node.priority,
          dueDate: node.dueDate,
          actorName: node.actorName,
          createdAt: node.createdAt,
          // Локальная пометка поверх серверного значения — только до ответа.
          readAt:
            node.readAt ??
            (pendingRead.has(String(node.id)) ? 'pending' : null),
        })),
    [page?.edges, pendingRead],
  );

  const unreadCount = Math.max(0, (page?.unreadCount ?? 0) - pendingRead.size);

  const loadMore = useCallback(() => {
    if (!page?.hasNextPage || !page?.endCursor) return;
    // `fetchMore` с курсором: страницы сшивает политика типа в кеше, ручной
    // `updateQuery` здесь запрещён — он строит склейку из ПРЕДЫДУЩЕГО
    // отрисованного результата и течёт между фильтрами (ТЗ 0043).
    fetchMore({ variables: { after: page.endCursor } });
  }, [fetchMore, page?.endCursor, page?.hasNextPage]);

  const markRead = useCallback(
    (ids: string[]) => {
      const fresh = ids.filter((id) => !pendingRead.has(id));
      if (!fresh.length) return;
      setPendingRead((current) => {
        const next = new Set(current);
        fresh.forEach((id) => next.add(id));
        return next;
      });
      markReadMutation({ variables: { ids: fresh } })
        .then(() => {
          refetch();
          onChanged();
        })
        .finally(() =>
          setPendingRead((current) => {
            const next = new Set(current);
            fresh.forEach((id) => next.delete(id));
            return next;
          }),
        );
    },
    [markReadMutation, onChanged, pendingRead, refetch],
  );

  const markAllRead = useCallback(() => {
    // Граница по времени снимается ЗДЕСЬ, в момент клика: уведомление,
    // пришедшее после отрисовки списка, гасить нельзя — человек его ещё не
    // видел, и оно исчезло бы навсегда.
    const before = new Date().toISOString();
    markAllMutation({ variables: { before } }).then(() => {
      refetch();
      onChanged();
    });
  }, [markAllMutation, onChanged, refetch]);

  return {
    rows,
    unreadCount,
    hasNextPage: Boolean(page?.hasNextPage),
    loading,
    error: Boolean(error),
    loadMore,
    markRead,
    markAllRead,
    refetch: () => {
      refetch();
    },
  };
};
